import React, { PureComponent } from 'react';

import * as Sentry from '@sentry/browser';

import FeatureGates from '@atlaskit/feature-gate-js-client';
import { StatsigFeatureKeys } from '@atlassian/bitbucket-features';
import { replaceBidiCharacters } from '@atlassian/bitkit-diff/exports/util/bidi-character-replacements';

import { SourceFileMonacoEditor } from 'src/components/bbc-source-editor/src/monaco-editor';
import * as store from 'src/components/navigation/src/navigation-store';
import { Repository } from 'src/components/types';
import {
  ConnectFileViewer,
  DEFAULT_EDITOR_KEY,
} from 'src/connect/file-modules';
import urls from 'src/urls/source';

import EditableFile from '../containers/editable-file';
import MarkupFileRenderer from '../containers/markup-file-renderer';
import { FileEditorMode, FileMeta } from '../types';
import { isImage, isBinary, isLFS } from '../utils/file';
import { getMarkupType } from '../utils/readme';

import BinaryFile from './binary-file';
import { FileContentsHeightProvider } from './file-contents-height-provider';
import * as styles from './file-contents.style';
import FileLoading from './file-loading';
import { RenderProps } from './markup-file-renderer';

type Props = {
  connectEditorModuleKey?: string;
  connectViewerModuleKey?: string;
  contents?: string;
  fileEditorMode: FileEditorMode;
  hash: string;
  isLoading?: boolean;
  meta?: FileMeta;
  onChangeFileEditorMode: (fileEditorMode: FileEditorMode) => void;
  onCommitChanges: (contents: string) => void;
  onSelectConnectEditor: (editor?: string) => void;
  path: string;
  repository: Repository;
  isColorBlindModeEnabled: boolean;
  isSyntaxHighlightingEnabled: boolean;
};

export class FileContents extends PureComponent<Props> {
  render() {
    const {
      connectEditorModuleKey,
      connectViewerModuleKey,
      contents,
      fileEditorMode,
      hash,
      isLoading,
      meta,
      onChangeFileEditorMode,
      onCommitChanges,
      onSelectConnectEditor,
      path,
      repository,
      isColorBlindModeEnabled,
      isSyntaxHighlightingEnabled,
    } = this.props;

    const isEditing = fileEditorMode !== 'read-only';

    const newSourceEditorEnabled = FeatureGates.checkGate(
      StatsigFeatureKeys.newSourceEditor
    );

    const theme = store.getColorTheme();

    if (isLoading || !meta) {
      return <FileLoading />;
    }

    if (
      connectViewerModuleKey &&
      connectViewerModuleKey !== DEFAULT_EDITOR_KEY &&
      !isEditing
    ) {
      return (
        <FileContentsHeightProvider>
          {({ height }) => (
            <ConnectFileViewer
              height={height}
              viewer={connectViewerModuleKey}
            />
          )}
        </FileContentsHeightProvider>
      );
    }

    const rawUrl = urls.ui.raw(repository.full_name, hash, path);

    // Images are binary but we can render them, so check that first
    if (isImage(meta)) {
      return (
        <styles.ImageWrapper>
          <img src={rawUrl} alt="Repository Logo" role="presentation" />
        </styles.ImageWrapper>
      );
    }

    const isLfs = isLFS(meta);
    if (isBinary(meta) || isLfs) {
      return (
        <BinaryFile
          repository={repository}
          hash={hash}
          path={path}
          isLfs={isLfs}
        />
      );
    }

    const isMarkup = !!getMarkupType(path);

    if (isMarkup && !isEditing) {
      return (
        <MarkupFileRenderer
          path={path}
          render={({ markupAsRawHtml }: RenderProps) => (
            <styles.MarkupWrapper
              dangerouslySetInnerHTML={{ __html: markupAsRawHtml }}
            />
          )}
        />
      );
    }

    if (contents === undefined) {
      return <FileLoading />;
    }

    let contentsWithCodePoint = contents;
    try {
      contentsWithCodePoint = replaceBidiCharacters(contents);
    } catch (e) {
      Sentry.captureException(e);
    }

    return (
      <FileContentsHeightProvider isEditing={isEditing}>
        {({ height }) =>
          newSourceEditorEnabled ? (
            <SourceFileMonacoEditor
              readOnly={fileEditorMode === 'read-only'}
              initialSource={contentsWithCodePoint}
              height={height}
              fileName={path}
              theme={theme}
            />
          ) : (
            <EditableFile
              connectEditorModuleKey={connectEditorModuleKey}
              contents={contentsWithCodePoint}
              fileEditorMode={fileEditorMode}
              hash={hash}
              height={height}
              isMarkup={isMarkup}
              onChangeFileEditorMode={onChangeFileEditorMode}
              onCommitChanges={onCommitChanges}
              onSelectConnectEditor={onSelectConnectEditor}
              path={path}
              isColorBlindModeEnabled={isColorBlindModeEnabled}
              isSyntaxHighlightingEnabled={isSyntaxHighlightingEnabled}
            />
          )
        }
      </FileContentsHeightProvider>
    );
  }
}
